import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ChevronRight, Check, BookmarkPlus, Heart } from "lucide-react";
import { toast } from "sonner";
import { logResetCompletion, rateReset } from "@/lib/resetLog";
import { addToolkitItem, isExerciseSaved } from "@/lib/toolkit";
import { track } from "@/lib/analytics";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Guided intervention player: intro → steps (optional countdowns) → completion.
export default function ResetPlayer({ intervention, state, premium, onExit }) {
  const [phase, setPhase] = useState("intro"); // intro | step | done
  const [stepIdx, setStepIdx] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [entry, setEntry] = useState(null);
  const [rated, setRated] = useState(null);
  const [saved, setSaved] = useState(() => isExerciseSaved(intervention.id));
  const timerRef = useRef(null);
  const reduceMotion = useReducedMotion();

  const step = intervention.steps[stepIdx];
  const isLast = stepIdx === intervention.steps.length - 1;
  const isBreathingStep =
    intervention.category === "breathing" && remaining !== null && remaining > 0;
  const isBoxRhythmStep =
    intervention.id === "ri_box_breath" && stepIdx === 2 && isBreathingStep;

  useEffect(() => {
    if (phase !== "step" || !step?.seconds) {
      setRemaining(null);
      return;
    }
    setRemaining(step.seconds);
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, stepIdx, step]);

  const begin = () => {
    track("reset_started", { state, intervention: intervention.id });
    setPhase("step");
  };

  const next = () => {
    if (isLast) {
      const e = logResetCompletion({ state, interventionId: intervention.id });
      setEntry(e);
      track("reset_completed", { state, intervention: intervention.id });
      setPhase("done");
    } else {
      setStepIdx((i) => i + 1);
    }
  };

  const handleRate = (value) => {
    setRated(value);
    if (entry) rateReset(entry.id, value);
    track("reset_completed", { rated: value, intervention: intervention.id });
  };

  const handleSave = () => {
    const res = addToolkitItem(
      { type: "exercise", label: intervention.title, detail: intervention.tagline, refId: intervention.id },
      { premium }
    );
    if (res.ok) {
      setSaved(true);
      toast("Kept in your Toolkit.", { description: "It'll be there next time you need it." });
    } else if (res.reason === "limit") {
      toast("Your Toolkit is full for now.", {
        description: "Free includes 8 saved items. Premium keeps space for everything that works for you.",
      });
    }
  };

  return (
    <motion.div
      data-testid="reset-player"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] overflow-y-auto"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #4A2D48 0%, #3A2338 45%, #2A1B2A 100%)",
      }}
    >
      <div className="un-grain absolute inset-0 pointer-events-none opacity-40" />
      <div className="relative max-w-[480px] mx-auto min-h-full flex flex-col px-6 pt-6 pb-10 un-safe-top un-safe-bottom">
        <div className="flex items-center justify-between">
          <span
            className="text-[10px] uppercase tracking-[0.3em]"
            style={{ color: "rgba(253,251,247,0.6)", fontFamily: "Manrope, sans-serif" }}
          >
            {intervention.minutes} min · {intervention.title}
          </span>
          <button
            data-testid="reset-player-exit"
            onClick={onExit}
            aria-label="Close"
            className="p-2 rounded-full un-press"
            style={{ color: "rgba(253,251,247,0.75)", background: "rgba(253,251,247,0.08)" }}
          >
            <X size={16} />
          </button>
        </div>

        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex-1 flex flex-col justify-center"
            >
              <h1
                className="text-[32px] leading-tight"
                style={{ color: "#FDFBF7", fontFamily: "'Lora', serif", fontWeight: 500 }}
              >
                {intervention.title}
              </h1>
              <p
                className="mt-3 text-[17px] italic leading-relaxed"
                style={{ color: "#D6B884", fontFamily: "'Cormorant Garamond', serif" }}
              >
                {intervention.tagline}
              </p>
              <p className="mt-5 text-[14px] leading-relaxed" style={{ color: "rgba(253,251,247,0.75)" }}>
                {intervention.steps.length} gentle steps · about {intervention.minutes}{" "}
                {intervention.minutes === 1 ? "minute" : "minutes"}. You can stop any time.
              </p>
              <button
                data-testid="reset-player-begin"
                onClick={begin}
                className="mt-9 w-full py-4 rounded-full text-[15px] font-medium un-press inline-flex items-center justify-center gap-2"
                style={{
                  background: "#FDFBF7",
                  color: "#2A1B2A",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                Begin <ChevronRight size={16} />
              </button>
            </motion.div>
          )}

          {phase === "step" && (
            <motion.div
              key={`step-${stepIdx}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex-1 flex flex-col justify-center"
            >
              <div className="flex gap-1.5 mb-8">
                {intervention.steps.map((_, i) => (
                  <span
                    key={i}
                    className="h-1 flex-1 rounded-full"
                    style={{
                      background: i <= stepIdx ? "#D6B884" : "rgba(253,251,247,0.18)",
                    }}
                  />
                ))}
              </div>
              <p
                data-testid="reset-player-step-text"
                className="text-[22px] leading-relaxed"
                style={{ color: "#FDFBF7", fontFamily: "'Lora', serif" }}
              >
                {step.text}
              </p>
              {isBreathingStep ? (
                <div
                  className="mt-8 flex flex-col items-center"
                  data-testid="reset-breathing-visual"
                  aria-label="Breathing visual guide"
                >
                  <motion.div
                    className="rounded-full flex items-center justify-center"
                    style={{
                      width: 112,
                      height: 112,
                      background:
                        "radial-gradient(circle at 35% 30%, rgba(247,236,211,0.96) 0%, rgba(214,184,132,0.82) 58%, rgba(196,161,106,0.42) 100%)",
                      boxShadow: "0 0 38px rgba(214,184,132,0.28)",
                    }}
                    animate={
                      reduceMotion
                        ? { scale: 1, opacity: 0.9 }
                        : isBoxRhythmStep
                          ? {
                              scale: [0.78, 1.08, 1.08, 0.78, 0.78],
                              opacity: [0.66, 1, 1, 0.66, 0.66],
                            }
                          : {
                              scale: [0.82, 1.08, 0.82],
                              opacity: [0.66, 1, 0.66],
                            }
                    }
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : isBoxRhythmStep
                          ? {
                              duration: 16,
                              repeat: Infinity,
                              ease: "easeInOut",
                              times: [0, 0.25, 0.5, 0.75, 1],
                            }
                          : { duration: 8, repeat: Infinity, ease: "easeInOut" }
                    }
                  >
                    <span
                      className="text-[12px] uppercase tracking-[0.22em]"
                      style={{ color: "#4A2D48", fontFamily: "Manrope, sans-serif" }}
                    >
                      Breathe
                    </span>
                  </motion.div>
                  <p
                    className="mt-4 text-[13px] text-center"
                    style={{
                      color: "rgba(253,251,247,0.72)",
                      fontFamily: "Manrope, sans-serif",
                    }}
                  >
                    Let the movement set the pace.
                  </p>
                </div>
              ) : remaining !== null && remaining > 0 ? (
                <p
                  className="mt-8 text-[40px] font-light tabular-nums"
                  style={{ color: "#D6B884", fontFamily: "Manrope, sans-serif" }}
                >
                  {fmt(remaining)}
                </p>
              ) : null}
              <button
                data-testid="reset-player-next"
                onClick={next}
                className="mt-10 w-full py-4 rounded-full text-[15px] font-medium un-press"
                style={{
                  background:
                    remaining > 0 && !isBreathingStep
                      ? "rgba(253,251,247,0.12)"
                      : "#FDFBF7",
                  color:
                    remaining > 0 && !isBreathingStep
                      ? "rgba(253,251,247,0.85)"
                      : "#2A1B2A",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {isBreathingStep
                  ? "Continue when you're ready"
                  : remaining > 0
                    ? "I'm ready to continue"
                    : isLast
                      ? "Complete"
                      : "Continue"}
              </button>
            </motion.div>
          )}

          {phase === "done" && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex-1 flex flex-col justify-center"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="mx-auto rounded-full flex items-center justify-center"
                style={{
                  width: 64,
                  height: 64,
                  background: "radial-gradient(circle at 35% 30%, #F7ECD3 0%, #D6B884 55%, #C4A16A 100%)",
                  boxShadow: "0 0 30px rgba(214,184,132,0.4)",
                }}
              >
                <Check size={26} style={{ color: "#2A1B2A" }} />
              </motion.div>
              <p
                data-testid="reset-player-closing"
                className="mt-8 text-center text-[20px] italic leading-relaxed"
                style={{ color: "#FDFBF7", fontFamily: "'Cormorant Garamond', serif", fontSize: "22px" }}
              >
                {intervention.closing}
              </p>

              <div className="mt-9">
                <p
                  className="text-center text-[12px] uppercase tracking-[0.25em] mb-3"
                  style={{ color: "rgba(253,251,247,0.55)", fontFamily: "Manrope, sans-serif" }}
                >
                  Did this help?
                </p>
                <div className="flex gap-3 justify-center">
                  {[
                    { id: "helped", label: "It helped", icon: Heart },
                    { id: "unsure", label: "Not sure yet", icon: null },
                  ].map((r) => (
                    <button
                      key={r.id}
                      data-testid={`reset-player-rate-${r.id}`}
                      onClick={() => handleRate(r.id)}
                      className="px-5 py-2.5 rounded-full text-[13px] un-press inline-flex items-center gap-2"
                      style={{
                        background: rated === r.id ? "#D6B884" : "rgba(253,251,247,0.10)",
                        color: rated === r.id ? "#2A1B2A" : "rgba(253,251,247,0.85)",
                        fontFamily: "Manrope, sans-serif",
                      }}
                    >
                      {r.icon && <r.icon size={13} />} {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {!saved && (
                <button
                  data-testid="reset-player-save-toolkit"
                  onClick={handleSave}
                  className="mt-6 mx-auto px-5 py-2.5 rounded-full text-[13px] un-press inline-flex items-center gap-2"
                  style={{
                    background: "rgba(253,251,247,0.08)",
                    color: "rgba(253,251,247,0.8)",
                    border: "1px solid rgba(253,251,247,0.18)",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  <BookmarkPlus size={14} /> Keep in my Toolkit
                </button>
              )}

              <button
                data-testid="reset-player-done"
                onClick={onExit}
                className="mt-9 w-full py-4 rounded-full text-[15px] font-medium un-press"
                style={{ background: "#FDFBF7", color: "#2A1B2A", fontFamily: "Manrope, sans-serif" }}
              >
                I'm steadier now
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
